import { kbToMb, mimeToExt } from '@/services/file/fileProcessor';
import { toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';

export type fileTypes = 'image' | 'file';

const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
const allowedExtensions = () => [...allowedImageTypes.map(mime => mimeToExt(mime)).filter(ext => ext), 'jpg'];
const maxAllowedSizeMb = 10;
export const allowedImageTypesInputTag = allowedImageTypes.join(',');

export const documentMimeTypes = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.apple.pages',
  'application/rtf',
  'text/rtf',
  'text/vnd.latex-z',
  'text/plain',
  'application/vnd.wordperfect',
  'application/pdf',
  'application/vnd.apple.numbers',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.publishare-delta-tree',
];
export const imageMimeTypes = [
  'image/bmp',
  'application/dicom',
  'image/vnd.ms-dds',
  'image/vnd.djvu',
  'image/gif',
  'image/heic',
  'image/jpeg',
  'image/png',
  'image/vnd.adobe.photoshop',
  'image/targa',
  'image/tiff',
  'image/svg+xml',
];
export const videoMimeTypes = [
  'video/mp4',
  'video/x-msvideo',
  'video/quicktime',
  'video/x-matroska',
  'video/x-ms-wmv',
  'video/x-flv',
  'video/x-m4v',
  'video/mpeg',
  'video/3gpp',
  'video/webm',
];
export const audioMimeTypes = [
  'audio/flac',
  'audio/m4a',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/x-ms-wma',
];

// after finishing the audio and video from the assets service we can add them to the allowed file types
const allowedFileTypes = [...documentMimeTypes, ...imageMimeTypes];

const allowedFileExtensions = () => [...allowedFileTypes.map(mime => mimeToExt(mime)).filter(ext => ext)];
const maxFileAllowedSizeMb = 100;
export const allowedFileTypesInputTag = allowedFileTypes.join(',');

export const validateFileExtension = (file: File, fileType : fileTypes = 'image') => {
  return (fileType === 'image' ? allowedImageTypes : allowedFileTypes).includes(file.type);
};

export const validateFileSize = (file: File, fileType : fileTypes = 'image') => {
  return kbToMb(file.size) <= (fileType === 'image' ? maxAllowedSizeMb : maxFileAllowedSizeMb);
};

export const validateFile = (file: File, fileType : fileTypes = 'image') => {
  if (!validateFileExtension(file, fileType)) {
    toasted.error(
      t(
        'assets.validation.extension',
        { allowedExtensions: (fileType === 'image' ? allowedExtensions() : allowedFileExtensions()).join(', ') },
      ),
      { duration: 5000 },
    );
    return false;
  }

  if (!validateFileSize(file, fileType)) {
    toasted.error(
      t(
        'assets.validation.weight',
        { maxAllowedSizeMb: (fileType === 'image' ? maxAllowedSizeMb : maxFileAllowedSizeMb).toString() },
      ),
      { duration: 5000 },
    );
    return false;
  }

  return true;
};

// Above code is a mess and needs to be refactored.

export type FileTypes = 'image' | 'audio' | 'video' | 'document';

const customMimeType: Record<string, string> = {
  'video/quicktime': 'mov',
};

export const customMimeToExt = (mimeType: string): string => {
  if (mimeType in customMimeType) {
    return customMimeType[mimeType];
  }

  return mimeToExt(mimeType);
};

export const getMimeTypes = (fileTypes: FileTypes[]): string[] => {
  const types = [...new Set(fileTypes)];

  return types
    .map(type => {
      switch (type) {
        case 'image':
          return imageMimeTypes;
        case 'audio':
          return audioMimeTypes;
        case 'video':
          return videoMimeTypes;
        case 'document':
          return documentMimeTypes;
        default:
          return [];
      }
    })
    .flat();
};

export const isFileSizeValid = (file: File, maxSizeMb = 100) => {
  return kbToMb(file.size) <= maxSizeMb;
};

export const extensionsWithNoMimeType = () => ['.mkv', '.dds', '.djvu', '.numbers', '.pages', '.tex'];
